import SalviaLayout from "@/components/SalviaLayout";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Plus, Search, Building2, Users, Edit, Trash2, RefreshCw, Loader2, ChevronDown, ChevronUp, UserPlus, X } from "lucide-react";
import { useEffect, useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { toast } from "sonner";
import { trpc } from "@/lib/trpc";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { ConfirmationModal } from "@/components/ConfirmationModal";
import { LogoUploadField } from "@/components/LogoUploadField";
import ChannelCatalogPanel from "@/components/ChannelCatalogPanel";
import { getEnabledChannelServiceIds, toggleChannelCatalogSelection } from "@/lib/channelCatalog";
import { buildPlatformContractsSummary } from "@/lib/platformContractsCache";
import { usePageTitle } from "@/hooks/usePageTitle";

// V17: Tipo para usuário a ser criado junto com o canal
type ChannelUserInput = {
  id: string; // chave local para React key
  name: string;
  email: string;
  role: "channel_admin" | "channel_operator" | "channel_consultant" | "";
  phone: string;
  serviceIds: number[];
};

const emptyChannelUser = (): ChannelUserInput => ({
  id: Math.random().toString(36).slice(2),
  name: "",
  email: "",
  role: "",
  phone: "",
  serviceIds: [],
});

const ESTADOS_BRASIL = [
  "AC", "AL", "AP", "AM", "BA", "CE", "DF", "ES", "GO", "MA", "MT", "MS", "MG",
  "PA", "PB", "PR", "PE", "PI", "RJ", "RN", "RS", "RO", "RR", "SC", "SP", "SE", "TO"
];

export default function Channels() {
  usePageTitle("Canais");
  const utils = trpc.useUtils();
  const [searchTerm, setSearchTerm] = useState("");
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [isEditDialogOpen, setIsEditDialogOpen] = useState(false);
  const [editingChannelId, setEditingChannelId] = useState<number | null>(null);
  const [showAddress, setShowAddress] = useState(false);
  // V17: Estado para usuários do canal (onboarding)
  const [channelUsers, setChannelUsers] = useState<ChannelUserInput[]>([]);
  const [userFormErrors, setUserFormErrors] = useState<Record<string, string>>({});
  // V50: Estado para adicionar novo usuário no Editar Canal
  const [editNewUser, setEditNewUser] = useState<ChannelUserInput | null>(null);
  const [editUserErrors, setEditUserErrors] = useState<Record<string, string>>({});

  // Modal de confirmacao para ativar/desativar
  const [confirmModal, setConfirmModal] = useState<{
    isOpen: boolean;
    action: 'activate' | 'deactivate' | null;
    channelId: number | null;
  }>({
    isOpen: false,
    action: null,
    channelId: null,
  });
  
  // Form state com todos os campos obrigatorios do documento
  const [formData, setFormData] = useState({
    // Dados basicos
    name: "",
    slug: "",
    cnpj: "",
    razaoSocial: "",
    nomeFantasia: "",
    // Endereco completo
    logradouro: "",
    numero: "",
    complemento: "",
    bairro: "",
    cidade: "",
    estado: "",
    cep: "",
    // Contato
    email: "",
    phone: "",
    responsibleName: "",
    // White-label
    primaryColor: "#002F59",
    accentColor: "#C0FF00",
    // V51: Flag de branding externo
    applyExternalWhitelabel: false,
  });
  const [draftCatalogServiceIds, setDraftCatalogServiceIds] = useState<number[]>([]);

  // tRPC queries
  const { data: channels, isLoading, refetch } = trpc.channels.list.useQuery();
  const { data: clientCounts } = trpc.clients.countByChannel.useQuery();
  const { data: createChannelCatalog, isLoading: isLoadingCreateChannelCatalog } = trpc.platformContracts.summary.useQuery(
    {},
    { enabled: isDialogOpen },
  );
  
  // tRPC mutations
  const createChannel = trpc.channels.create.useMutation({
    onSuccess: () => {
      toast.success("Canal cadastrado com sucesso!");
      setIsDialogOpen(false);
      resetForm();
      refetch();
    },
    onError: (error) => {
      toast.error(error.message || "Erro ao cadastrar canal");
    },
  });

  // V17: Criar canal com usuários vinculados
  const createChannelWithUsers = trpc.channels.createWithUsers.useMutation({
    onSuccess: (data) => {
      if (data.usersFailed > 0) {
        const failedList = data.userResults
          .filter(r => !r.success)
          .map(r => `${r.email}: ${r.error}`)
          .join("\n");
        toast.warning(
          `Canal criado! ${data.usersCreated} usuário(s) criado(s), ${data.usersFailed} falha(s):\n${failedList}`,
          { duration: 8000 }
        );
      } else if (data.usersCreated > 0) {
        toast.success(`Canal criado com sucesso! ${data.usersCreated} usuário(s) criado(s) e e-mail(s) de boas-vindas enviado(s).`);
      } else {
        toast.success("Canal cadastrado com sucesso!");
      }
      setIsDialogOpen(false);
      resetForm();
      refetch();
    },
    onError: (error) => {
      toast.error(error.message || "Erro ao cadastrar canal");
    },
  });

  // Catálogo comercial do canal em edição
  const { data: editingChannelCatalog, refetch: refetchEditingChannelCatalog, isLoading: isLoadingEditingChannelCatalog } = trpc.platformContracts.summary.useQuery(
    { channelId: editingChannelId ?? undefined },
    { enabled: !!editingChannelId }
  );

  useEffect(() => {
    if (!editingChannelCatalog?.services) return;
    setDraftCatalogServiceIds(getEnabledChannelServiceIds(editingChannelCatalog.services));
  }, [editingChannelCatalog?.services, editingChannelId]);

  useEffect(() => {
    if (isDialogOpen) {
      setDraftCatalogServiceIds([]);
    }
  }, [isDialogOpen]);

  // V50: Query para listar usuários do canal em edição
  const { data: channelUsersData, refetch: refetchChannelUsers } = trpc.channels.listUsers.useQuery(
    { channelId: editingChannelId! },
    { enabled: !!editingChannelId }
  );

  // V50: Mutations para gerenciar usuários do canal
  const addUserToChannel = trpc.channels.addUser.useMutation({
    onSuccess: () => {
      toast.success("Usuário adicionado ao canal! E-mail de convite enviado.");
      setEditNewUser(null);
      setEditUserErrors({});
      refetchChannelUsers();
    },
    onError: (error) => {
      toast.error(error.message || "Erro ao adicionar usuário");
    },
  });

  const removeUserFromChannel = trpc.channels.removeUser.useMutation({
    onSuccess: () => {
      toast.success("Usuário removido do canal.");
      refetchChannelUsers();
    },
    onError: (error) => {
      toast.error(error.message || "Erro ao remover usuário");
    },
  });

  const updateUserRoleInChannel = trpc.channels.updateUserRole.useMutation({
    onSuccess: () => {
      toast.success("Perfil de acesso atualizado.");
      refetchChannelUsers();
    },
    onError: (error) => {
      toast.error(error.message || "Erro ao atualizar perfil");
    },
  });

  const updateChannel = trpc.channels.update.useMutation();

  const setChannelCatalog = trpc.platformContracts.setChannelCatalog.useMutation({
    onSuccess: (data) => {
      toast.success("Serviços habilitados do canal atualizados com sucesso!");
      setDraftCatalogServiceIds(data.enabledServiceIds);
      utils.platformContracts.summary.setData(
        { channelId: data.channelId },
        (current) =>
          buildPlatformContractsSummary({
            companyId: current?.scope.companyId ?? null,
            channelId: data.channelId,
            services: data.services,
            contracts: current?.contracts ?? [],
          }),
      );
      void utils.platformContracts.summary.invalidate({ channelId: data.channelId });
    },
    onError: (error) => {
      toast.error(error.message || "Não foi possível atualizar os serviços habilitados do canal.");
    },
  });

  const isUpdatingChannelFlow = updateChannel.isPending || setChannelCatalog.isPending;

  const deleteChannel = trpc.channels.delete.useMutation({
    onSuccess: () => {
      toast.success("Canal desativado com sucesso.\nOs usuários vinculados não possuem mais acesso ao sistema.");
      setConfirmModal({ isOpen: false, action: null, channelId: null });
      refetch();
    },
    onError: (error) => {
      const errorMsg = error.message || "Não foi possível alterar o status do canal no momento. Tente novamente ou entre em contato com o suporte.";
      toast.error(errorMsg);
    },
  });

  const reactivateChannel = trpc.channels.reactivate.useMutation({
    onSuccess: () => {
      toast.success("Canal ativado com sucesso.\nOs usuários vinculados já podem acessar o sistema novamente.");
      setConfirmModal({ isOpen: false, action: null, channelId: null });
      refetch();
    },
    onError: (error) => {
      const errorMsg = error.message || "Não foi possível alterar o status do canal no momento. Tente novamente ou entre em contato com o suporte.";
      toast.error(errorMsg);
    },
  });

  const uploadLogo = trpc.channels.uploadLogo.useMutation({
    onSuccess: () => {
      toast.success("Logo enviada com sucesso!");
      refetch();
    },
    onError: (error) => {
      toast.error(error.message || "Erro ao enviar logo");
    },
  });

  const removeLogo = trpc.channels.removeLogo.useMutation({
    onSuccess: () => {
      toast.success("Logo removida com sucesso!");
      refetch();
    },
    onError: (error) => {
      toast.error(error.message || "Erro ao remover logo");
    },
  });

  const handleUploadLogo = async (channelId: number, file: File) => {
    const reader = new FileReader();
    reader.onload = () => {
      const base64 = (reader.result as string).split(",")[1];
      uploadLogo.mutate({
        channelId,
        fileName: file.name,
        mimeType: file.type as "image/png" | "image/jpeg" | "image/webp",
        base64Data: base64,
        fileSize: file.size,
      });
    };
    reader.readAsDataURL(file);
  };

  const handleRemoveLogo = async (channelId: number) => {
    removeLogo.mutate({ channelId });
  };

  const resetForm = () => {
    setFormData({
      name: "",
      slug: "",
      cnpj: "",
      razaoSocial: "",
      nomeFantasia: "",
      logradouro: "",
      numero: "",
      complemento: "",
      bairro: "",
      cidade: "",
      estado: "",
      cep: "",
      email: "",
      phone: "",
      responsibleName: "",
      primaryColor: "#002F59",
      accentColor: "#C0FF00",
      applyExternalWhitelabel: false,
    });
    setShowAddress(false);
    setDraftCatalogServiceIds([]);
    // V17: limpar usuários do canal
    setChannelUsers([]);
    setUserFormErrors({});
  };

  // V17: Validar lista de usuários antes de enviar
  const validateChannelUsers = (): boolean => {
    const errors: Record<string, string> = {};
    const emailsSeen = new Set<string>();
    let valid = true;
    channelUsers.forEach((u) => {
      if (!u.name.trim()) {
        errors[`${u.id}_name`] = "Nome obrigatório";
        valid = false;
      }
      if (!u.email.trim()) {
        errors[`${u.id}_email`] = "E-mail obrigatório";
        valid = false;
      } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(u.email)) {
        errors[`${u.id}_email`] = "E-mail inválido";
        valid = false;
      } else if (emailsSeen.has(u.email.toLowerCase())) {
        errors[`${u.id}_email`] = "E-mail duplicado na lista";
        valid = false;
      } else {
        emailsSeen.add(u.email.toLowerCase());
      }
      if (!u.role) {
        errors[`${u.id}_role`] = "Perfil obrigatório";
        valid = false;
      }
    });
    setUserFormErrors(errors);
    return valid;
  };

  const filteredChannels = (channels || []).filter(
    (channel) =>
      channel.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      (channel.cnpj && channel.cnpj.includes(searchTerm))
  );

  const handleCreateChannel = () => {
    if (!formData.name || !formData.slug || !formData.razaoSocial || !formData.nomeFantasia) {
      toast.error("Nome, Slug, Razão Social e Nome Fantasia são obrigatórios");
      return;
    }
    // V17: Validar usuários antes de enviar
    if (!validateChannelUsers()) {
      toast.error("Corrija os erros nos usuários antes de continuar.");
      return;
    }
    // V17: Usar createWithUsers (lista vazia = comportamento idêntico ao create original)
    createChannelWithUsers.mutate({
      name: formData.name,
      slug: formData.slug,
      cnpj: formData.cnpj || undefined,
      razaoSocial: formData.razaoSocial,
      nomeFantasia: formData.nomeFantasia,
      logradouro: formData.logradouro || undefined,
      numero: formData.numero || undefined,
      complemento: formData.complemento || undefined,
      bairro: formData.bairro || undefined,
      cidade: formData.cidade || undefined,
      estado: formData.estado || undefined,
      cep: formData.cep || undefined,
      email: formData.email || undefined,
      phone: formData.phone || undefined,
      responsibleName: formData.responsibleName || undefined,
      primaryColor: formData.primaryColor,
      accentColor: formData.accentColor,
      catalogServiceIds: draftCatalogServiceIds,
      channelUsers: channelUsers
        .filter(u => u.role !== "")
        .map(u => ({
          name: u.name,
          email: u.email,
          role: u.role as "channel_admin" | "channel_operator" | "channel_consultant",
          phone: u.phone || undefined,
        })),
    });
  };

  const handleEditChannel = (channel: any) => {
    setEditingChannelId(channel.id);
    setFormData({
      name: channel.name || "",
      slug: channel.slug || "",
      cnpj: channel.cnpj || "",
      razaoSocial: channel.razaoSocial || "",
      nomeFantasia: channel.nomeFantasia || "",
      logradouro: channel.logradouro || "",
      numero: channel.numero || "",
      complemento: channel.complemento || "",
      bairro: channel.bairro || "",
      cidade: channel.cidade || "",
      estado: channel.estado || "",
      cep: channel.cep || "",
      email: channel.email || "",
      phone: channel.phone || "",
      responsibleName: channel.responsibleName || "",
      primaryColor: channel.primaryColor || "#002F59",
      accentColor: channel.accentColor || "#C0FF00",
      // V51: Carregar flag de branding externo
      applyExternalWhitelabel: Boolean(channel.applyExternalWhitelabel),
    });
    setIsEditDialogOpen(true);
  };

  const handleSaveChannelCatalog = () => {
    if (!editingChannelId) {
      toast.error("Selecione um canal válido para atualizar o catálogo.");
      return;
    }

    setChannelCatalog.mutate({
      channelId: editingChannelId,
      serviceIds: draftCatalogServiceIds,
    });
  };

  const handleUpdateChannel = async () => {
    if (!editingChannelId) return;
    if (!formData.name || !formData.slug || !formData.razaoSocial || !formData.nomeFantasia) {
      toast.error("Nome, Slug, Razão Social e Nome Fantasia são obrigatórios");
      return;
    }

    try {
      const channelId = editingChannelId;

      await updateChannel.mutateAsync({
        id: channelId,
        data: {
          name: formData.name,
          slug: formData.slug,
          cnpj: formData.cnpj || undefined,
          razaoSocial: formData.razaoSocial,
          nomeFantasia: formData.nomeFantasia,
          logradouro: formData.logradouro || undefined,
          numero: formData.numero || undefined,
          complemento: formData.complemento || undefined,
          bairro: formData.bairro || undefined,
          cidade: formData.cidade || undefined,
          estado: formData.estado || undefined,
          cep: formData.cep || undefined,
          email: formData.email || undefined,
          phone: formData.phone || undefined,
          responsibleName: formData.responsibleName || undefined,
          primaryColor: formData.primaryColor,
          accentColor: formData.accentColor,
          // V51: Flag de branding externo
          applyExternalWhitelabel: formData.applyExternalWhitelabel,
        },
      });

      const catalogResult = await setChannelCatalog.mutateAsync({
        channelId,
        serviceIds: draftCatalogServiceIds,
      });

      setDraftCatalogServiceIds(catalogResult.enabledServiceIds);
      await Promise.all([refetch(), refetchEditingChannelCatalog()]);
      toast.success("Canal e serviços atualizados com sucesso!");
      setIsEditDialogOpen(false);
      setEditingChannelId(null);
      resetForm();
    } catch (error: any) {
      toast.error(error?.message || "Não foi possível atualizar o canal e seus serviços.");
    }
  };

  const handleDeleteChannel = (id: number) => {
    setConfirmModal({
      isOpen: true,
      action: 'deactivate',
      channelId: id,
    });
  };

  const handleReactivateChannel = (id: number) => {
    setConfirmModal({
      isOpen: true,
      action: 'activate',
      channelId: id,
    });
  };
  
  const handleConfirmAction = () => {
    if (confirmModal.action === 'deactivate' && confirmModal.channelId) {
      deleteChannel.mutate({ id: confirmModal.channelId });
    } else if (confirmModal.action === 'activate' && confirmModal.channelId) {
      reactivateChannel.mutate({ id: confirmModal.channelId });
    }
  };
  
  const handleCancelAction = () => {
    setConfirmModal({
      isOpen: false,
      action: null,
      channelId: null,
    });
  };

  const generateSlug = (name: string) => {
    return name
      .toLowerCase()
      .normalize("NFD")
      .replace(/[\u0300-\u036f]/g, "")
      .replace(/[^a-z0-9]+/g, "-")
      .replace(/(^-|-$)/g, "");
  };

  const formatCNPJ = (value: string) => {
    const numbers = value.replace(/\D/g, "");
    return numbers.replace(
      /^(\d{2})(\d{3})(\d{3})(\d{4})(\d{2})$/,
      "$1.$2.$3/$4-$5"
    );
  };

  const formatCEP = (value: string) => {
    const numbers = value.replace(/\D/g, "");
    return numbers.replace(/^(\d{5})(\d{3})$/, "$1-$2");
  };

  const formatPhone = (value: string) => {
    const numbers = value.replace(/\D/g, "");
    if (numbers.length <= 10) {
      return numbers.replace(/^(\d{2})(\d{4})(\d{4})$/, "($1) $2-$3");
    }
    return numbers.replace(/^(\d{2})(\d{5})(\d{4})$/, "($1) $2-$3");
  };

  const totalClients = Object.values(clientCounts || {}).reduce((acc, count) => acc + count, 0);
  const activeChannels = (channels || []).filter((c) => c.status === "active").length;

  if (isLoading) {
    return (
      <SalviaLayout title="Gestao de Canais">
        <div className="flex items-center justify-center h-64">
          <Loader2 className="w-8 h-8 animate-spin text-[#002F59]" />
        </div>
      </SalviaLayout>
    );
  }

  return (
    <SalviaLayout title="Gestao de Canais">
      <div className="space-y-6">
        {/* Header Actions */}
        <div className="flex flex-col sm:flex-row justify-between gap-4">
          <div className="relative flex-1 max-w-md">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={20} />
            <Input
              placeholder="Buscar por nome ou CNPJ..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-10"
            />
          </div>
          
          <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
            <DialogTrigger asChild>
              <Button className="bg-[#C0FF00] text-[#002F59] hover:bg-[#b0e600] font-semibold">
                <Plus size={20} className="mr-2" />
                Novo Canal
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
              <DialogHeader>
                <DialogTitle>Cadastrar Novo Canal</DialogTitle>
              </DialogHeader>
              
              <div className="space-y-4">
                {/* Dados Basicos */}
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="text-sm font-semibold text-gray-700">Nome do Canal *</label>
                    <Input
                      placeholder="Nome do canal"
                      value={formData.name}
                      onChange={(e) => {
                        setFormData({ ...formData, name: e.target.value });
                        setFormData((prev) => ({ ...prev, slug: generateSlug(e.target.value) }));
                      }}
                    />
                  </div>
                  <div>
                    <label className="text-sm font-semibold text-gray-700">Slug *</label>
                    <Input placeholder="slug-do-canal" value={formData.slug} readOnly className="bg-gray-100" />
                  </div>
                </div>

                {/* Dados Comerciais */}
                <div className="grid grid-cols-3 gap-4">
                  <div>
                    <label className="text-sm font-semibold text-gray-700">CNPJ</label>
                    <Input
                      placeholder="00.000.000/0000-00"
                      value={formData.cnpj}
                      onChange={(e) => setFormData({ ...formData, cnpj: formatCNPJ(e.target.value) })}
                    />
                  </div>
                  <div>
                    <label className="text-sm font-semibold text-gray-700">Razao Social</label>
                    <Input
                      placeholder="Razao Social"
                      value={formData.razaoSocial}
                      onChange={(e) => setFormData({ ...formData, razaoSocial: e.target.value })}
                    />
                  </div>
                  <div>
                    <label className="text-sm font-semibold text-gray-700">Nome Fantasia</label>
                    <Input
                      placeholder="Nome Fantasia"
                      value={formData.nomeFantasia}
                      onChange={(e) => setFormData({ ...formData, nomeFantasia: e.target.value })}
                    />
                  </div>
                </div>

                {/* Endereco */}
                <button
                  type="button"
                  onClick={() => setShowAddress(!showAddress)}
                  className="flex items-center gap-2 text-sm font-semibold text-[#002F59] hover:text-[#C0FF00]"
                >
                  {showAddress ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
                  Endereco Completo
                </button>

                {showAddress && (
                  <div className="space-y-4 p-4 bg-gray-50 rounded-lg">
                    <div className="grid grid-cols-3 gap-4">
                      <div>
                        <label className="text-sm font-semibold text-gray-700">Logradouro</label>
                        <Input
                          placeholder="Rua, Avenida..."
                          value={formData.logradouro}
                          onChange={(e) => setFormData({ ...formData, logradouro: e.target.value })}
                        />
                      </div>
                      <div>
                        <label className="text-sm font-semibold text-gray-700">Numero</label>
                        <Input
                          placeholder="123"
                          value={formData.numero}
                          onChange={(e) => setFormData({ ...formData, numero: e.target.value })}
                        />
                      </div>
                      <div>
                        <label className="text-sm font-semibold text-gray-700">Complemento</label>
                        <Input
                          placeholder="Apto, Sala..."
                          value={formData.complemento}
                          onChange={(e) => setFormData({ ...formData, complemento: e.target.value })}
                        />
                      </div>
                    </div>

                    <div className="grid grid-cols-4 gap-4">
                      <div>
                        <label className="text-sm font-semibold text-gray-700">Bairro</label>
                        <Input
                          placeholder="Bairro"
                          value={formData.bairro}
                          onChange={(e) => setFormData({ ...formData, bairro: e.target.value })}
                        />
                      </div>
                      <div>
                        <label className="text-sm font-semibold text-gray-700">Cidade</label>
                        <Input
                          placeholder="Cidade"
                          value={formData.cidade}
                          onChange={(e) => setFormData({ ...formData, cidade: e.target.value })}
                        />
                      </div>
                      <div>
                        <label className="text-sm font-semibold text-gray-700">Estado</label>
                        <Select value={formData.estado} onValueChange={(value) => setFormData({ ...formData, estado: value })}>
                          <SelectTrigger>
                            <SelectValue placeholder="Selecione" />
                          </SelectTrigger>
                          <SelectContent>
                            {ESTADOS_BRASIL.map((estado) => (
                              <SelectItem key={estado} value={estado}>{estado}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                      <div>
                        <label className="text-sm font-semibold text-gray-700">CEP</label>
                        <Input
                          placeholder="00000-000"
                          value={formData.cep}
                          onChange={(e) => setFormData({ ...formData, cep: formatCEP(e.target.value) })}
                        />
                      </div>
                    </div>
                  </div>
                )}

                {/* Contato */}
                <div className="grid grid-cols-3 gap-4">
                  <div>
                    <label className="text-sm font-semibold text-gray-700">Email</label>
                    <Input
                      placeholder="contato@canal.com"
                      type="email"
                      value={formData.email}
                      onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                    />
                  </div>
                  <div>
                    <label className="text-sm font-semibold text-gray-700">Telefone</label>
                    <Input
                      placeholder="(11) 99999-9999"
                      value={formData.phone}
                      onChange={(e) => setFormData({ ...formData, phone: formatPhone(e.target.value) })}
                    />
                  </div>
                  <div>
                    <label className="text-sm font-semibold text-gray-700">Responsavel</label>
                    <Input
                      placeholder="Nome do responsavel"
                      value={formData.responsibleName}
                      onChange={(e) => setFormData({ ...formData, responsibleName: e.target.value })}
                    />
                  </div>
                </div>

                {/* White-label */}
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="text-sm font-semibold text-gray-700">Cor Primaria</label>
                    <div className="flex gap-2">
                      <input
                        type="color"
                        value={formData.primaryColor}
                        onChange={(e) => setFormData({ ...formData, primaryColor: e.target.value })}
                        className="w-12 h-10 rounded cursor-pointer"
                      />
                      <Input
                        placeholder="#002F59"
                        value={formData.primaryColor}
                        onChange={(e) => setFormData({ ...formData, primaryColor: e.target.value })}
                      />
                    </div>
                  </div>
                  <div>
                    <label className="text-sm font-semibold text-gray-700">Cor de Destaque</label>
                    <div className="flex gap-2">
                      <input
                        type="color"
                        value={formData.accentColor}
                        onChange={(e) => setFormData({ ...formData, accentColor: e.target.value })}
                        className="w-12 h-10 rounded cursor-pointer"
                      />
                      <Input
                        placeholder="#C0FF00"
                        value={formData.accentColor}
                        onChange={(e) => setFormData({ ...formData, accentColor: e.target.value })}
                      />
                    </div>
                  </div>
                </div>

                <ChannelCatalogPanel
                  channelLabel={formData.nomeFantasia || formData.razaoSocial || formData.name || "Novo canal"}
                  channelContextLabel="Canal em cadastro"
                  servicesInCatalog={draftCatalogServiceIds.length}
                  servicesContracted={0}
                  services={createChannelCatalog?.services || []}
                  draftServiceIds={draftCatalogServiceIds}
                  isLoading={isLoadingCreateChannelCatalog}
                  isSaving={createChannelWithUsers.isPending}
                  saveButtonLabel="Serviços serão salvos ao cadastrar"
                  saveButtonDisabled
                  onSave={() => undefined}
                  onToggleService={(serviceId, checked) => {
                    setDraftCatalogServiceIds((prev) => toggleChannelCatalogSelection(prev, serviceId, checked));
                  }}
                />

                {/* Logo — disponível apenas após criar o canal */}
                <div className="p-3 bg-muted/30 rounded-lg">
                  <p className="text-sm text-muted-foreground">A logo do canal poderá ser anexada após a criação, clicando em "Editar".</p>
                </div>

                {/* V17: Seção Usuários do Canal */}
                <div className="border border-dashed border-gray-300 rounded-lg p-4 space-y-3">
                  <div className="flex items-center justify-between">
                    <div>
                      <h4 className="text-sm font-semibold text-gray-800 flex items-center gap-2">
                        <Users size={16} />
                        Usuários do Canal
                      </h4>
                      <p className="text-xs text-muted-foreground mt-0.5">
                        Opcional — adicione usuários que receberão acesso ao canal imediatamente após a criação.
                      </p>
                    </div>
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      onClick={() => setChannelUsers(prev => [...prev, emptyChannelUser()])}
                      className="flex items-center gap-1 text-xs"
                    >
                      <UserPlus size={14} />
                      Adicionar Usuário
                    </Button>
                  </div>

                  {channelUsers.length === 0 && (
                    <p className="text-xs text-muted-foreground text-center py-2">
                      Nenhum usuário adicionado. O canal será criado sem usuários vinculados.
                    </p>
                  )}

                  {channelUsers.map((user, idx) => (
                    <div key={user.id} className="bg-gray-50 rounded-lg p-3 space-y-2">
                      <div className="flex items-center justify-between">
                        <span className="text-xs font-medium text-gray-600">Usuário {idx + 1}</span>
                        <button
                          type="button"
                          onClick={() => setChannelUsers(prev => prev.filter(u => u.id !== user.id))}
                          className="text-gray-400 hover:text-red-500 transition-colors"
                        >
                          <X size={14} />
                        </button>
                      </div>
                      <div className="grid grid-cols-2 gap-2">
                        <div>
                          <Input
                            placeholder="Nome completo *"
                            value={user.name}
                            onChange={(e) => setChannelUsers(prev =>
                              prev.map(u => u.id === user.id ? { ...u, name: e.target.value } : u)
                            )}
                            className={userFormErrors[`${user.id}_name`] ? "border-red-500" : ""}
                          />
                          {userFormErrors[`${user.id}_name`] && (
                            <p className="text-xs text-red-500 mt-0.5">{userFormErrors[`${user.id}_name`]}</p>
                          )}
                        </div>
                        <div>
                          <Input
                            placeholder="E-mail *"
                            type="email"
                            value={user.email}
                            onChange={(e) => setChannelUsers(prev =>
                              prev.map(u => u.id === user.id ? { ...u, email: e.target.value } : u)
                            )}
                            className={userFormErrors[`${user.id}_email`] ? "border-red-500" : ""}
                          />
                          {userFormErrors[`${user.id}_email`] && (
                            <p className="text-xs text-red-500 mt-0.5">{userFormErrors[`${user.id}_email`]}</p>
                          )}
                        </div>
                      </div>
                      <div className="grid grid-cols-2 gap-2">
                        <div>
                          <Select
                            value={user.role}
                            onValueChange={(val) => setChannelUsers(prev =>
                              prev.map(u => u.id === user.id ? { ...u, role: val as ChannelUserInput["role"] } : u)
                            )}
                          >
                            <SelectTrigger className={userFormErrors[`${user.id}_role`] ? "border-red-500" : ""}>
                              <SelectValue placeholder="Perfil de acesso *" />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="channel_admin">Admin do Canal</SelectItem>
                              <SelectItem value="channel_operator">Operador do Canal</SelectItem>
                              <SelectItem value="channel_consultant">Consultor do Canal</SelectItem>
                            </SelectContent>
                          </Select>
                          {userFormErrors[`${user.id}_role`] && (
                            <p className="text-xs text-red-500 mt-0.5">{userFormErrors[`${user.id}_role`]}</p>
                          )}
                        </div>
                        <div>
                          <Input
                            placeholder="Telefone (opcional)"
                            value={user.phone}
                            onChange={(e) => setChannelUsers(prev =>
                              prev.map(u => u.id === user.id ? { ...u, phone: e.target.value } : u)
                            )}
                          />
                        </div>
                      </div>
                    </div>
                  ))}
                </div>

                <Button
                  onClick={handleCreateChannel}
                  disabled={createChannelWithUsers.isPending}
                  className="w-full bg-[#C0FF00] text-[#002F59] hover:bg-[#b0e600] font-semibold"
                >
                  {createChannelWithUsers.isPending ? <Loader2 className="mr-2 animate-spin" size={20} /> : null}
                  {channelUsers.length > 0
                    ? `Cadastrar Canal + ${channelUsers.length} Usuário${channelUsers.length > 1 ? "s" : ""}`
                    : "Cadastrar Canal"}
                </Button>
              </div>
            </DialogContent>
          </Dialog>
        </div>

        {/* Edit Dialog */}
        <Dialog open={isEditDialogOpen} onOpenChange={(open) => {
          setIsEditDialogOpen(open);
          if (!open) {
            setEditingChannelId(null);
            setDraftCatalogServiceIds([]);
          }
        }}>
          <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>Editar Canal</DialogTitle>
            </DialogHeader>
            
            <div className="space-y-4">
              {/* Dados Basicos */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="text-sm font-semibold text-gray-700">Nome do Canal *</label>
                  <Input
                    placeholder="Nome do canal"
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  />
                </div>
                <div>
                  <label className="text-sm font-semibold text-gray-700">Slug *</label>
                  <Input placeholder="slug-do-canal" value={formData.slug} readOnly className="bg-gray-100" />
                </div>
              </div>

              {/* Dados Comerciais */}
              <div className="grid grid-cols-3 gap-4">
                <div>
                  <label className="text-sm font-semibold text-gray-700">CNPJ</label>
                  <Input
                    placeholder="00.000.000/0000-00"
                    value={formData.cnpj}
                    onChange={(e) => setFormData({ ...formData, cnpj: formatCNPJ(e.target.value) })}
                  />
                </div>
                <div>
                  <label className="text-sm font-semibold text-gray-700">Razao Social</label>
                  <Input
                    placeholder="Razao Social"
                    value={formData.razaoSocial}
                    onChange={(e) => setFormData({ ...formData, razaoSocial: e.target.value })}
                  />
                </div>
                <div>
                  <label className="text-sm font-semibold text-gray-700">Nome Fantasia</label>
                  <Input
                    placeholder="Nome Fantasia"
                    value={formData.nomeFantasia}
                    onChange={(e) => setFormData({ ...formData, nomeFantasia: e.target.value })}
                  />
                </div>
              </div>

              {/* Endereco */}
              <button
                type="button"
                onClick={() => setShowAddress(!showAddress)}
                className="flex items-center gap-2 text-sm font-semibold text-[#002F59] hover:text-[#C0FF00]"
              >
                {showAddress ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
                Endereco Completo
              </button>

              {showAddress && (
                <div className="space-y-4 p-4 bg-gray-50 rounded-lg">
                  <div className="grid grid-cols-3 gap-4">
                    <div>
                      <label className="text-sm font-semibold text-gray-700">Logradouro</label>
                      <Input
                        placeholder="Rua, Avenida..."
                        value={formData.logradouro}
                        onChange={(e) => setFormData({ ...formData, logradouro: e.target.value })}
                      />
                    </div>
                    <div>
                      <label className="text-sm font-semibold text-gray-700">Numero</label>
                      <Input
                        placeholder="123"
                        value={formData.numero}
                        onChange={(e) => setFormData({ ...formData, numero: e.target.value })}
                      />
                    </div>
                    <div>
                      <label className="text-sm font-semibold text-gray-700">Complemento</label>
                      <Input
                        placeholder="Apto, Sala..."
                        value={formData.complemento}
                        onChange={(e) => setFormData({ ...formData, complemento: e.target.value })}
                      />
                    </div>
                  </div>

                  <div className="grid grid-cols-4 gap-4">
                    <div>
                      <label className="text-sm font-semibold text-gray-700">Bairro</label>
                      <Input
                        placeholder="Bairro"
                        value={formData.bairro}
                        onChange={(e) => setFormData({ ...formData, bairro: e.target.value })}
                      />
                    </div>
                    <div>
                      <label className="text-sm font-semibold text-gray-700">Cidade</label>
                      <Input
                        placeholder="Cidade"
                        value={formData.cidade}
                        onChange={(e) => setFormData({ ...formData, cidade: e.target.value })}
                      />
                    </div>
                    <div>
                      <label className="text-sm font-semibold text-gray-700">Estado</label>
                      <Select value={formData.estado} onValueChange={(value) => setFormData({ ...formData, estado: value })}>
                        <SelectTrigger>
                          <SelectValue placeholder="Selecione" />
                        </SelectTrigger>
                        <SelectContent>
                          {ESTADOS_BRASIL.map((estado) => (
                            <SelectItem key={estado} value={estado}>{estado}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <label className="text-sm font-semibold text-gray-700">CEP</label>
                      <Input
                        placeholder="00000-000"
                        value={formData.cep}
                        onChange={(e) => setFormData({ ...formData, cep: formatCEP(e.target.value) })}
                      />
                    </div>
                  </div>
                </div>
              )}

              {/* Contato */}
              <div className="grid grid-cols-3 gap-4">
                <div>
                  <label className="text-sm font-semibold text-gray-700">Email</label>
                  <Input
                    placeholder="contato@canal.com"
                    type="email"
                    value={formData.email}
                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  />
                </div>
                <div>
                  <label className="text-sm font-semibold text-gray-700">Telefone</label>
                  <Input
                    placeholder="(11) 99999-9999"
                    value={formData.phone}
                    onChange={(e) => setFormData({ ...formData, phone: formatPhone(e.target.value) })}
                  />
                </div>
                <div>
                  <label className="text-sm font-semibold text-gray-700">Responsavel</label>
                  <Input
                    placeholder="Nome do responsavel"
                    value={formData.responsibleName}
                    onChange={(e) => setFormData({ ...formData, responsibleName: e.target.value })}
                  />
                </div>
              </div>

              {/* White-label */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="text-sm font-semibold text-gray-700">Cor Primaria</label>
                  <div className="flex gap-2">
                    <input
                      type="color"
                      value={formData.primaryColor}
                      onChange={(e) => setFormData({ ...formData, primaryColor: e.target.value })}
                      className="w-12 h-10 rounded cursor-pointer"
                    />
                    <Input
                      placeholder="#002F59"
                      value={formData.primaryColor}
                      onChange={(e) => setFormData({ ...formData, primaryColor: e.target.value })}
                    />
                  </div>
                </div>
                <div>
                  <label className="text-sm font-semibold text-gray-700">Cor de Destaque</label>
                  <div className="flex gap-2">
                    <input
                      type="color"
                      value={formData.accentColor}
                      onChange={(e) => setFormData({ ...formData, accentColor: e.target.value })}
                      className="w-12 h-10 rounded cursor-pointer"
                    />
                    <Input
                      placeholder="#C0FF00"
                      value={formData.accentColor}
                      onChange={(e) => setFormData({ ...formData, accentColor: e.target.value })}
                    />
                  </div>
                </div>
              </div>

              {/* Logo do Canal */}
              {editingChannelId && (
                <LogoUploadField
                  currentLogoUrl={(channels || []).find((c) => c.id === editingChannelId)?.logoUrl}
                  onUpload={async (file) => handleUploadLogo(editingChannelId, file)}
                  onRemove={async () => handleRemoveLogo(editingChannelId)}
                  isUploading={uploadLogo.isPending}
                  isRemoving={removeLogo.isPending}
                />
              )}

              {/* V51: Switch de Branding Externo (Admin Master only) */}
              <div className="border border-dashed border-gray-300 rounded-lg p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <h4 className="text-sm font-semibold text-gray-800">Aplicar White-label em Documentos</h4>
                    <p className="text-xs text-muted-foreground mt-0.5">
                      Quando ativado, PDFs e questionários usam logo e cores do canal. Quando desativado, usam branding Sálvia.
                    </p>
                  </div>
                  <label className="relative inline-flex items-center cursor-pointer">
                    <input
                      type="checkbox"
                      checked={formData.applyExternalWhitelabel ?? true}
                      onChange={(e) => setFormData({ ...formData, applyExternalWhitelabel: e.target.checked })}
                      className="sr-only peer"
                    />
                    <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-2 peer-focus:ring-[#002F59] rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-[#002F59]"></div>
                    <span className="ml-2 text-sm font-medium text-gray-700">
                      {formData.applyExternalWhitelabel ? 'Ativado' : 'Desativado'}
                    </span>
                  </label>
                </div>
              </div>

              {/* Catálogo comercial do Canal */}
              {editingChannelId && (
                <ChannelCatalogPanel
                  channelLabel={formData.nomeFantasia || formData.razaoSocial || formData.name || `Canal #${editingChannelId}`}
                  servicesInCatalog={editingChannelCatalog?.metrics?.servicesInCatalog ?? 0}
                  servicesContracted={editingChannelCatalog?.metrics?.servicesContracted ?? 0}
                  services={editingChannelCatalog?.services || []}
                  draftServiceIds={draftCatalogServiceIds}
                  isLoading={isLoadingEditingChannelCatalog}
                  isSaving={isUpdatingChannelFlow}
                  onSave={handleSaveChannelCatalog}
                  onToggleService={(serviceId, checked) => {
                    setDraftCatalogServiceIds((prev) => toggleChannelCatalogSelection(prev, serviceId, checked));
                  }}
                />
              )}

              {/* V50: Seção Usuários do Canal */}
              {editingChannelId && (
                <div className="border border-dashed border-gray-300 rounded-lg p-4 space-y-3">
                  <div className="flex items-center justify-between">
                    <div>
                      <h4 className="text-sm font-semibold text-gray-800 flex items-center gap-2">
                        <Users size={16} />
                        Usuários do Canal
                      </h4>
                      <p className="text-xs text-muted-foreground mt-0.5">
                        Gerencie os usuários vinculados a este canal.
                      </p>
                    </div>
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      onClick={() => setEditNewUser(emptyChannelUser())}
                      className="flex items-center gap-1 text-xs"
                      disabled={!!editNewUser}
                    >
                      <UserPlus size={14} />
                      Adicionar Usuário
                    </Button>
                  </div>

                  {/* Lista de usuários existentes */}
                  {(!channelUsersData || channelUsersData.length === 0) && !editNewUser && (
                    <p className="text-xs text-muted-foreground text-center py-2">
                      Nenhum usuário vinculado a este canal.
                    </p>
                  )}

                  {(channelUsersData || []).filter(u => u.isActive).map((user) => (
                    <div key={user.id} className="bg-gray-50 rounded-lg p-3 flex items-center justify-between gap-3">
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium text-gray-800 truncate">{user.name}</p>
                        <p className="text-xs text-gray-500 truncate">{user.email}</p>
                        {user.phone && <p className="text-xs text-gray-400">{user.phone}</p>}
                      </div>
                      <div className="flex items-center gap-2">
                        <Select
                          value={user.role}
                          onValueChange={(val) => {
                            updateUserRoleInChannel.mutate({
                              channelId: editingChannelId,
                              userId: user.id,
                              role: val as "channel_admin" | "channel_operator" | "channel_consultant",
                            });
                          }}
                        >
                          <SelectTrigger className="w-[160px] h-8 text-xs">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="channel_admin">Admin do Canal</SelectItem>
                            <SelectItem value="channel_operator">Operador do Canal</SelectItem>
                            <SelectItem value="channel_consultant">Consultor do Canal</SelectItem>
                          </SelectContent>
                        </Select>
                        <button
                          type="button"
                          onClick={() => {
                            if (confirm(`Remover ${user.name} deste canal?`)) {
                              removeUserFromChannel.mutate({
                                channelId: editingChannelId,
                                userId: user.id,
                              });
                            }
                          }}
                          className="text-gray-400 hover:text-red-500 transition-colors p-1"
                          title="Remover usuário"
                        >
                          <Trash2 size={14} />
                        </button>
                      </div>
                    </div>
                  ))}

                  {/* Formulário para adicionar novo usuário */}
                  {editNewUser && (
                    <div className="bg-blue-50 rounded-lg p-3 space-y-2 border border-blue-200">
                      <div className="flex items-center justify-between">
                        <span className="text-xs font-medium text-blue-700">Novo Usuário</span>
                        <button
                          type="button"
                          onClick={() => { setEditNewUser(null); setEditUserErrors({}); }}
                          className="text-gray-400 hover:text-red-500 transition-colors"
                        >
                          <X size={14} />
                        </button>
                      </div>
                      <div className="grid grid-cols-2 gap-2">
                        <div>
                          <Input
                            placeholder="Nome completo *"
                            value={editNewUser.name}
                            onChange={(e) => setEditNewUser({ ...editNewUser, name: e.target.value })}
                            className={editUserErrors.name ? "border-red-500" : ""}
                          />
                          {editUserErrors.name && <p className="text-xs text-red-500 mt-0.5">{editUserErrors.name}</p>}
                        </div>
                        <div>
                          <Input
                            placeholder="E-mail *"
                            type="email"
                            value={editNewUser.email}
                            onChange={(e) => setEditNewUser({ ...editNewUser, email: e.target.value })}
                            className={editUserErrors.email ? "border-red-500" : ""}
                          />
                          {editUserErrors.email && <p className="text-xs text-red-500 mt-0.5">{editUserErrors.email}</p>}
                        </div>
                      </div>
                      <div className="grid grid-cols-2 gap-2">
                        <div>
                          <Select
                            value={editNewUser.role || undefined}
                            onValueChange={(val) => setEditNewUser({ ...editNewUser, role: val as ChannelUserInput["role"] })}
                          >
                            <SelectTrigger className={editUserErrors.role ? "border-red-500" : ""}>
                              <SelectValue placeholder="Perfil de acesso *" />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="channel_admin">Admin do Canal</SelectItem>
                              <SelectItem value="channel_operator">Operador do Canal</SelectItem>
                              <SelectItem value="channel_consultant">Consultor do Canal</SelectItem>
                            </SelectContent>
                          </Select>
                          {editUserErrors.role && <p className="text-xs text-red-500 mt-0.5">{editUserErrors.role}</p>}
                        </div>
                        <div>
                          <Input
                            placeholder="Telefone (opcional)"
                            value={editNewUser.phone}
                            onChange={(e) => setEditNewUser({ ...editNewUser, phone: e.target.value })}
                          />
                        </div>
                      </div>
                      <div>
                        <p className="text-xs font-medium text-gray-600 mb-1">Serviços liberados para o usuário</p>
                        {draftCatalogServiceIds.length === 0 && (
                          <p className="text-xs text-muted-foreground">
                            Nenhum serviço habilitado no catálogo deste canal.
                          </p>
                        )}
                        <div className="flex flex-wrap gap-2">
                          {(editingChannelCatalog?.services || [])
                            .filter((service) => draftCatalogServiceIds.includes(service.id))
                            .map((service) => {
                              const checked = editNewUser.serviceIds.includes(service.id);
                              return (
                                <label
                                  key={service.id}
                                  className="flex items-center gap-1.5 text-xs bg-white border border-gray-200 rounded-full px-2.5 py-1 cursor-pointer"
                                >
                                  <input
                                    type="checkbox"
                                    checked={checked}
                                    onChange={(e) => {
                                      setEditNewUser({
                                        ...editNewUser,
                                        serviceIds: e.target.checked
                                          ? [...editNewUser.serviceIds, service.id]
                                          : editNewUser.serviceIds.filter((id) => id !== service.id),
                                      });
                                    }}
                                  />
                                  {service.name}
                                </label>
                              );
                            })}
                        </div>
                      </div>
                      <Button
                        type="button"
                        size="sm"
                        disabled={addUserToChannel.isPending}
                        className="bg-[#002F59] text-white hover:bg-[#001f3d] w-full"
                        onClick={() => {
                          const errors: Record<string, string> = {};
                          if (!editNewUser.name.trim()) errors.name = "Nome obrigatório";
                          if (!editNewUser.email.trim()) errors.email = "E-mail obrigatório";
                          else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(editNewUser.email)) errors.email = "E-mail inválido";
                          if (!editNewUser.role) errors.role = "Perfil obrigatório";
                          setEditUserErrors(errors);
                          if (Object.keys(errors).length > 0) return;
                          addUserToChannel.mutate({
                            channelId: editingChannelId,
                            name: editNewUser.name,
                            email: editNewUser.email,
                            role: editNewUser.role as "channel_admin" | "channel_operator" | "channel_consultant",
                            phone: editNewUser.phone || undefined,
                            serviceIds: editNewUser.serviceIds,
                          });
                        }}
                      >
                        {addUserToChannel.isPending ? <Loader2 className="mr-2 animate-spin" size={14} /> : null}
                        Adicionar e Enviar Convite
                      </Button>
                    </div>
                  )}
                </div>
              )}

              <Button
                onClick={handleUpdateChannel}
                disabled={isUpdatingChannelFlow}
                className="w-full bg-[#C0FF00] text-[#002F59] hover:bg-[#b0e600] font-semibold"
              >
                {isUpdatingChannelFlow ? <Loader2 className="mr-2 animate-spin" size={20} /> : null}
                Atualizar Canal
              </Button>
            </div>
          </DialogContent>
        </Dialog>

        {/* Stats */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <Card className="p-6 border-l-4 border-l-[#C0FF00]">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">Canais Ativos</p>
                <p className="text-3xl font-bold text-[#002F59]">{activeChannels}</p>
              </div>
              <Building2 size={40} className="text-[#C0FF00]" />
            </div>
          </Card>
          
          <Card className="p-6 border-l-4 border-l-[#C0FF00]">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">Total de Clientes</p>
                <p className="text-3xl font-bold text-[#002F59]">{totalClients}</p>
              </div>
              <Users size={40} className="text-[#C0FF00]" />
            </div>
          </Card>

          <Card className="p-6 border-l-4 border-l-[#C0FF00]">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">Taxa de Ocupacao</p>
                <p className="text-3xl font-bold text-[#002F59]">{activeChannels > 0 ? Math.round((totalClients / (activeChannels * 10)) * 100) : 0}%</p>
              </div>
              <RefreshCw size={40} className="text-[#C0FF00]" />
            </div>
          </Card>
        </div>

        {/* Lista de Canais */}
        <div className="space-y-4">
          <h2 className="text-xl font-bold text-[#002F59]">Canais Cadastrados</h2>
          
          {filteredChannels.length === 0 ? (
            <Card className="p-8 text-center">
              <p className="text-gray-500">Nenhum canal encontrado</p>
            </Card>
          ) : (
            <div className="grid gap-4">
              {filteredChannels.map((channel) => (
                <Card key={channel.id} className="p-6 hover:shadow-lg transition-shadow">
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <div className="flex items-center gap-3 mb-2">
                        {channel.logoUrl ? (
                          <img
                            src={channel.logoUrl}
                            alt={`Logo ${channel.name}`}
                            className="w-8 h-8 object-contain rounded"
                            onError={(e) => { (e.target as HTMLImageElement).style.display = 'none'; }}
                          />
                        ) : (
                          <div
                            className="w-4 h-4 rounded-full"
                            style={{ backgroundColor: channel.primaryColor || "#002F59" }}
                          />
                        )}
                        <h3 className="text-lg font-bold text-[#002F59]">{channel.name}</h3>
                        <span className={`px-2 py-1 text-xs font-semibold rounded ${
                          channel.status === "active"
                            ? "bg-green-100 text-green-800"
                            : "bg-gray-100 text-gray-800"
                        }`}>
                          {channel.status === "active" ? "Ativo" : "Inativo"}
                        </span>
                      </div>
                      
                      <p className="text-sm text-gray-600 mb-2">
                        <strong>CNPJ:</strong> {channel.cnpj || "Nao informado"}
                      </p>
                      
                      {(channel as any).razaoSocial && (
                        <p className="text-sm text-gray-600 mb-2">
                          <strong>Razao Social:</strong> {(channel as any).razaoSocial}
                        </p>
                      )}
                      
                      <p className="text-sm text-gray-600">
                        <strong>Clientes:</strong> {clientCounts?.[channel.id] || 0}
                      </p>
                    </div>
                    
                    <div className="flex gap-2">
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => handleEditChannel(channel)}
                        className="border-[#002F59] text-[#002F59] hover:bg-[#002F59] hover:text-white"
                      >
                        <Edit size={16} className="mr-1" />
                        Editar
                      </Button>
                      
                      {channel.status === "active" ? (
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => handleDeleteChannel(channel.id)}
                          className="border-red-500 text-red-500 hover:bg-red-500 hover:text-white"
                        >
                          <Trash2 size={16} className="mr-1" />
                          Desativar
                        </Button>
                      ) : (
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => handleReactivateChannel(channel.id)}
                          className="border-green-500 text-green-500 hover:bg-green-500 hover:text-white"
                        >
                          <RefreshCw size={16} className="mr-1" />
                          Reativar
                        </Button>
                      )}
                    </div>
                  </div>
                </Card>
              ))}
            </div>
          )}
        </div>
      </div>
      
      {/* Modal de Confirmacao para Ativar/Desativar */}
      <ConfirmationModal
        isOpen={confirmModal.isOpen}
        title={confirmModal.action === 'deactivate' ? 'Desativar canal' : 'Ativar canal'}
        description={
          confirmModal.action === 'deactivate'
            ? 'Ao desativar este canal, todos os usuários vinculados perderão acesso ao sistema imediatamente.\nOs dados e documentos permanecerão armazenados para fins de auditoria.\nDeseja continuar?'
            : 'Este canal será reativado e os usuários vinculados voltarão a ter acesso ao sistema.\nDeseja continuar?'
        }
        confirmText={confirmModal.action === 'deactivate' ? 'Desativar' : 'Ativar'}
        isDangerous={confirmModal.action === 'deactivate'}
        isLoading={deleteChannel.isPending || reactivateChannel.isPending}
        onConfirm={handleConfirmAction}
        onCancel={handleCancelAction}
      />
    </SalviaLayout>
  );
}
